// 2) Third-party library imports
import {
    createAction,
    createSlice,
    PayloadAction
} from '@reduxjs/toolkit';

// 3c) Project-specific modules - components / types
import { EntityType, ImportHistory, ImportStagingMessage, ImportState } from './types';
import { PageData } from '../common/types';
import type { RootState } from '../storeTypes';

// 3d) Utility functions / interfaces
import { DynamicObject } from '../../utils/commonInterfaces';

const actionPrefix = 'import';

// Type-safe initialState!
export const initialState: ImportState = {
    history: {
        loading: false,
        errorMessages: [],
        data: [],
        entityType: undefined,
        activeData: {
            record: {},
            errorsAndWarnings: {
                data: [],
                pageData: {
                    currentPage: 0,
                    pageSize: 0,
                    hasNextPage: false,
                },
                loading: false,
                errorMessages: [],
            },
            loading: false,
            selectedId: null,
            errorMessages: [],
        },
    },
};

const importSlice = createSlice({
    name: 'import',
    initialState,
    reducers: {
        getImportHistoryRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{ 
                    entityType: EntityType,
                    currentPage: number,
                    pageSize: number
                }>
            ) => {
                state.history.loading = true;
            },
            prepare: (
                entityType: EntityType,
                currentPage: number,
                pageSize: number
            ) => ({
                payload: {
                    entityType,
                    currentPage,
                    pageSize
                }
            })
        },
        getImportHistorySuccessAction: (
            state,
            action: PayloadAction<{
                data: ImportHistory[];
                pageData: PageData;
            }>
        ) => {
            const { data, pageData } = action.payload;

            if (pageData.currentPage === 0) {
                state.history.data = data; // replace
            } else {
                state.history.data.push(...data); // append
            }

            state.history.loading = false;
            state.history.errorMessages = initialState.history.errorMessages;
            state.history.pageData = pageData;

        },
        getImportHistoryErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.history.loading = false;
            state.history.data = initialState.history.data;
            state.history.errorMessages = action.payload;
        },
        setImportHistorySelectedIdRequestAction: {
            reducer: (
                state,
                action: PayloadAction<{
                    importHistoryId: string | null;
                    callback: () => void;
                }>
            ) => {
                state.history.activeData.selectedId = initialState.history.activeData.selectedId;
            },
            prepare: (
                importHistoryId: string | null,
                callback: () => void
            ) => ({
                payload: {
                    importHistoryId,
                    callback
                }
            })
        },
        setImportHistorySelectedIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.history.activeData.selectedId = action.payload;
        },
        getImportHistoryDataRequestAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.history.activeData.record = initialState.history.activeData.record;
            state.history.activeData.loading = true;
        },
        getImportHistoryDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.history.activeData.record = action.payload.record; 
            state.history.activeData.loading = false;
            state.history.activeData.errorMessages = [];
        },
        getImportHistoryDataErrorsAndWarningsRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    importJobId: string,
                    currentPage: number,
                    pageSize: number,
                    entityType: string
                }>
            ) => {
                state.history.activeData.errorsAndWarnings.loading = true;
            },
            prepare: (
                importJobId: string,
                currentPage: number,
                pageSize: number,
                entityType: string
            ) => ({
                payload: {
                    importJobId,
                    currentPage,
                    pageSize,
                    entityType
                }
            })
        },
        getImportHistoryDataErrorsAndWarningsSuccessAction: (
            state,
            action: PayloadAction<{
                data: ImportStagingMessage[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.history.activeData.errorsAndWarnings.data = action.payload.data;
            } else {
                state.history.activeData.errorsAndWarnings.data = [
                    ...state.history.activeData.errorsAndWarnings.data,
                    ...action.payload.data
                ];
            }

            state.history.activeData.errorsAndWarnings.pageData = action.payload.pageData;
            state.history.activeData.errorsAndWarnings.loading = false;
            state.history.activeData.errorsAndWarnings.errorMessages = [];
        },
        getImportHistoryDataErrorsAndWarningsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.history.activeData.errorsAndWarnings.data = [];
            state.history.activeData.errorsAndWarnings.loading = false;
            state.history.activeData.errorsAndWarnings.errorMessages = action.payload as [];
        },
        getImportHistoryDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.history.activeData.record = {};
            state.history.activeData.loading = false;
            state.history.activeData.errorMessages = action.payload;
        },
        clearImportHistoryDataAction: (state) => {
            state.history.activeData.record = initialState.history.activeData.record;
            state.history.activeData.loading = initialState.history.activeData.loading;
            state.history.activeData.selectedId = initialState.history.activeData.selectedId;
            state.history.activeData.errorMessages = [];
        },
        setImportHistoryEntityTypeAction: (
            state, 
            action: PayloadAction<EntityType>
        ) => {
            state.history.entityType = action.payload;
        }
    }
});

const { actions, reducer } = importSlice;

// Actions
export const {
    getImportHistoryRequestAction,
    getImportHistorySuccessAction,
    getImportHistoryErrorAction,
    setImportHistorySelectedIdRequestAction,
    setImportHistorySelectedIdSuccessAction,
    getImportHistoryDataRequestAction,
    getImportHistoryDataSuccessAction,
    getImportHistoryDataErrorsAndWarningsRequestAction,
    getImportHistoryDataErrorsAndWarningsSuccessAction,
    getImportHistoryDataErrorsAndWarningsErrorAction,
    getImportHistoryDataErrorAction,
    clearImportHistoryDataAction,
    setImportHistoryEntityTypeAction
} = actions;

export const uploadTemplateFileRequestAction = createAction(
    `${actionPrefix}/uploadTemplateFileRequestAction`,
    function prepare(
        type: string | undefined,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                type,
                callback
            }
        };
    }
);

export const downloadTemplateFileRequestAction = createAction(
    `${actionPrefix}/downloadTemplateFileRequestAction`,
    function prepare(
        fileName: string,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                fileName,
                callback
            }
        };
    }
);

export const getCheckImportJobStatusRequestAction = createAction(
    `${actionPrefix}/getCheckImportJobStatusRequestAction`,
    function prepare(
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                callback
            }
        };
    }
);

export const getXeroConfigurationRequestAction = createAction(
    `${actionPrefix}/getXeroConfigurationRequestAction`,
    function prepare(
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                callback
            }
        };
    }
);

export const xeroConnectRequestAction = createAction(
    `${actionPrefix}/xeroConnectRequestAction`,
    function prepare(
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                callback
            }
        };
    }
);

export const xeroDisconnectRequestAction = createAction(
    `${actionPrefix}/xeroDisconnectRequestAction`,
    function prepare(
        connectionId: string,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                connectionId,
                callback
            }
        };
    }
);

export const xeroManualSyncRequestAction = createAction(
    `${actionPrefix}/xeroManualSyncRequestAction`,
    function prepare(
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                callback
            }
        };
    }
);

export const uploadRemittanceAdviceTemplateFileRequestAction = createAction(
    `${actionPrefix}/uploadRemittanceAdviceTemplateFileRequestAction`,
    function prepare(
        type: string | undefined,
        remittanceAdviceId: string | undefined,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                type,
                remittanceAdviceId,
                callback
            }
        };
    }
);

export const myobConnectRequestAction = createAction(
    `${actionPrefix}/myobConnectRequestAction`,
    function prepare(
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                callback
            }
        };
    }
);

export const getMYOBConfigurationRequestAction = createAction(
    `${actionPrefix}/getMYOBConfigurationRequestAction`,
    function prepare(
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                callback
            }
        };
    }
);

export const myobDisconnectRequestAction = createAction(
    `${actionPrefix}/myobDisconnectRequestAction`,
    function prepare(
        connectionId: string,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                connectionId,
                callback
            }
        };
    }
);

export const myobConnectMYOBCompanyRequestAction = createAction(
    `${actionPrefix}/myobConnectMYOBCompanyRequestAction`,
    function prepare(
        selectedMYOBCompany: {},
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                selectedMYOBCompany,
                callback
            }
        };
    }
);

export const myobManualSyncRequestAction = createAction(
    `${actionPrefix}/myobManualSyncRequestAction`,
    function prepare(
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                callback
            }
        };
    }
);

export const getMYOBCompanySettingsRequestAction = createAction(
    `${actionPrefix}/getMYOBCompanySettingsRequestAction`,
    function prepare(
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                callback
            }
        };
    }
);

// Selectors
export const getImportsState = (state: RootState) => state.import;
export const getCurrentEntityType = (state: RootState) => state.import.history.entityType;
export const getSelectedImportHistoryId = (state: RootState) => state.import.history.activeData.selectedId;
export const getImportHistory = (state: RootState) => state.import.history;
export const getImportHistoryData = (state: RootState) => state.import.history.activeData;
export const getImportHistoryList = (state: RootState) => state.import.history.data;
// Reducer
export default reducer;